import { ArrowLeft, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";
import { PROTEIN_FIBER_SCORE_BANDS } from "../../utils/healthInsightScoreBands";
import { HealthInsightScoreBandsList } from "../../components/HealthInsights/HealthInsightScoreBandsList";
import { formatHealthInsightScore, hasHealthInsightScore } from "../../utils/healthInsightsMetrics";

const MetabolicStabilityDetail = () => {
  const navigate = useNavigate();

  // Use date-aware home data (reads ?date= query param or location state)
  const {
    data: homeData,
    isLoading: loading,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get metabolic stability data from API
  const insightData = homeData?.["Health Insights"]?.protein_fiber_ratio_daily;

  const proteinFiberScore = insightData?.score;
  const proteinFiberScoreText = formatHealthInsightScore(proteinFiberScore);
  const hasScore = hasHealthInsightScore(proteinFiberScore);
  const status = !hasScore ? "No Data" : (insightData?.label ?? "—");
  const label = "Protein-Fiber Ratio";

  const getProgressPercentage = () => {
    if (!hasScore) return 0;
    return Math.max(0, Math.min(100, proteinFiberScore!));
  };

  const getProgressColor = (): string => {
    if (!hasScore) return "bg-gray-300";
    if (proteinFiberScore! >= 61) return "bg-green-500";
    if (proteinFiberScore! >= 21) return "bg-blue-500";
    if (proteinFiberScore! >= 1) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-footnote text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-extrabold text-foreground">
            {formattedSelectedDate} {label || "Protein-Fiber Ratio"}
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-white p-5 shadow-lg dark:border-purple-800/30 dark:bg-card">
          <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-purple-400 to-violet-500" />
          
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/40">
              <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-subhead font-semibold text-muted-foreground">Today's Protein-Fiber Ratio</p>
                <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-bold uppercase tracking-wider ${
                  !hasScore
                    ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                    : status === "Good" || status === "Excellent"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                    : status === "Needs Improvement" || status === "Needs Attention" || status === "Moderate"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                }`}>
                  {status}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-foreground">
                  {proteinFiberScoreText ?? "—"}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/40">
            <div className="mb-3 flex justify-between text-subhead font-medium">
              <span className="text-muted-foreground">Daily Progress</span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor()}`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>
        </div>

        {/* What is Metabolic Stability */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            What is Protein-Fiber Ratio?
          </h3>
          <p className="text-callout leading-relaxed text-muted-foreground">
            The Protein-to-Fiber Ratio measures the balance between protein
            intake and dietary fiber. A balanced ratio supports muscle recovery
            while maintaining gut health and digestive regularity. Too much
            protein relative to fiber can lead to digestive issues, while
            adequate fiber helps with nutrient absorption and sustained energy.
          </p>
          <div className="mt-4">
            <p className="mb-2 text-subhead font-semibold text-foreground">Score Ranges</p>
            <HealthInsightScoreBandsList bands={PROTEIN_FIBER_SCORE_BANDS} />
          </div>
        </Card>

        {/* Tips to Improve Metabolic Stability */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 text-title3 font-bold text-foreground">
            Ways to Improve Your Ratio
          </h3>
          <ul className="space-y-2 text-callout text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Pair protein sources with high-fiber vegetables at every meal
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Add beans, lentils, or chickpeas as protein-fiber combo foods
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Include chia seeds, flaxseeds, or hemp seeds in smoothies
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Choose whole grains over refined options to boost fiber intake
              </span>
            </li>
          </ul>
        </Card>

      </div>
    </div>
  );
};

export default MetabolicStabilityDetail;
